/** @file writeKey: writes one key into a clip, replacing or merging, keeping keys sorted by frame. */
import type { Clip, ClipKey } from '@zilo/pose3d-format/types/clip';
import { compact } from '../validate/compact';

const isEmpty = (k: ClipKey): boolean => Object.keys(k.nodes).length === 0 && (k.events ?? []).length === 0;

/**
 * `replace` stores `key` as given; `mergeNodes` merges its nodes into the frame's key and keeps that key's `ease`,
 * `hold` and `events` unless `key` carries them. A resulting key with no nodes and no events is deleted.
 */
export function writeKey(clip: Clip, key: ClipKey, merge: 'replace' | 'mergeNodes'): void {
  const prev = clip.keys.find((k) => k.frame === key.frame);
  const next = structuredClone(merge === 'replace' || prev === undefined ? key : compact({
    frame: key.frame, ease: key.ease ?? prev.ease, hold: key.hold ?? prev.hold,
    nodes: { ...prev.nodes, ...key.nodes }, events: key.events ?? prev.events,
  }));
  if (next.events?.length === 0) delete next.events;
  const others = clip.keys.filter((k) => k.frame !== key.frame);
  clip.keys = (isEmpty(next) ? others : [...others, next]).sort((a, b) => a.frame - b.frame);
}
